import { z } from "zod/v4"
import { DocumentSnapshotLike } from "../firestore"
import { LiveCommentId, LiveCommentIdSchema, LiveId, LiveIdSchema, SpaceKey, SpaceKeySchema, UserId } from "../id"
import { CreatedAt } from "./system"
import { DeletedBy, DeletedBySchema } from "./space-message-deletion"

/**
 * LiveComment の削除イベントを表すドキュメント。
 *
 * クライアントはこのコレクションを購読することで、
 * 購読範囲外のコメント削除を検知できる。
 */
export const LiveCommentDeletionDocumentBody = z.object({
  space_key: SpaceKeySchema.describe("削除されたコメントが属するスペースのID"),
  live_id: LiveIdSchema.describe("削除されたコメントが属するライブのID"),
  deleted_by: DeletedBySchema.describe("削除を実行した主体"),
  created_at: CreatedAt.describe("削除日時 (UNIX Timestamp mills)"),
})

export type LiveCommentDeletionDocumentBody = z.infer<typeof LiveCommentDeletionDocumentBody>

export const LiveCommentDeletionDocument = z.intersection(
  LiveCommentDeletionDocumentBody,
  z.object({
    id: LiveCommentIdSchema.describe("削除されたコメントのID（ドキュメントIDと同一）"),
  })
)

export type LiveCommentDeletionDocument = z.infer<typeof LiveCommentDeletionDocument>

export class LiveCommentDeletionDocuments {
  static toDocumentData(doc: LiveCommentDeletionDocumentBody): object {
    return {
      ...doc,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): LiveCommentDeletionDocument | null {
    if (!doc) {
      return null
    }

    const data = doc.data()
    if (data === undefined) {
      return null
    }

    const deletedByData = data["deleted_by"]
    const deletedBy: DeletedBy = deletedByData.kind === "system"
      ? { kind: "system" }
      : { kind: "user", value: UserId(deletedByData.value) }

    return {
      id: LiveCommentId(doc.id),
      space_key: SpaceKey(data["space_key"]),
      live_id: LiveId(data["live_id"]),
      deleted_by: deletedBy,
      created_at: data["created_at"],
    }
  }
}
